import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Card, CardBody, Button, Input, FormGroup, Label,
  Table, Modal, ModalHeader, ModalBody, ModalFooter,
  DropdownToggle, DropdownMenu, DropdownItem, ButtonDropdown,
  Alert,
} from 'reactstrap';
import {
  ArrowLeft, Upload, Download, Plus, Trash2, Search,
  Users, ChevronDown, X, AlertCircle, RefreshCw,
} from 'react-feather';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { alertErro } from '../../utils/alerts';
import { useProspectAuth } from '../../Auth/ProspectAuthContext';

// ── Constantes ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

const HEADER_PT = {
  phone: 'Telefone', telephone: 'Telefone', mobile: 'Celular', cell: 'Celular',
  cel: 'Celular', whatsapp: 'WhatsApp', number: 'Número', numero: 'Número',
  name: 'Nome', nome: 'Nome', contact: 'Contato', contato: 'Contato',
  first_name: 'Primeiro Nome', last_name: 'Sobrenome', full_name: 'Nome Completo',
  email: 'E-mail', mail: 'E-mail',
  company: 'Empresa', empresa: 'Empresa',
  folder: 'Pasta', pasta: 'Pasta',
  city: 'Cidade', cidade: 'Cidade',
  segment: 'Segmento', segmento: 'Segmento',
};

function translateHeader(h) {
  const key = String(h).toLowerCase().trim();
  return HEADER_PT[key] || h;
}

// ── Componente principal ─────────────────────────────────────────────────────

export default function ListaDetalhe() {
  const { id } = useParams();
  const isNew   = id === 'nova';
  const navigate = useNavigate();
  const { user } = useProspectAuth();
  const isAdmin  = user?.role === 'admin';

  const [name, setName]           = useState('');
  const [tenants, setTenants]     = useState([]);
  const [tenantId, setTenantId]   = useState('');
  const [contacts, setContacts]   = useState([]);
  const [totalContacts, setTotal] = useState(0);
  const [offset, setOffset]       = useState(0);
  const [search, setSearch]       = useState('');
  const [searchDebounced, setSearchD] = useState('');
  const [loading, setLoading]     = useState(!isNew);
  const [saving, setSaving]       = useState(false);
  const [notice, setNotice]       = useState(null); // { type: 'success'|'warn', msg }

  // Modals
  const [manualModal, setManualModal]   = useState(false);
  const [manualForm, setManualForm]     = useState({ ddd: '', numero: '', name: '', email: '' });
  const [manualLoading, setManualLoad]  = useState(false);
  const [manualError, setManualError]   = useState('');

  const [importDropOpen, setImportDrop] = useState(false);
  const [exportDropOpen, setExportDrop] = useState(false);

  const [fileModal, setFileModal]       = useState(false);
  const [filePreview, setFilePreview]   = useState(null);
  const [filePending, setFilePending]   = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [colMap, setColMap]             = useState({ nameIdx: -1, phoneIdx: -1, emailIdx: -1 });

  const [groupModal, setGroupModal]     = useState(false);
  const [groups, setGroups]             = useState([]);
  const [groupsLoading, setGroupsLoad]  = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupImporting, setGroupImport]  = useState(false);

  const [waModal, setWaModal]           = useState(false);
  const [waContacts, setWaContacts]     = useState([]);
  const [waLoading, setWaLoad]          = useState(false);
  const [waSearch, setWaSearch]         = useState('');
  const [waSelected, setWaSelected]     = useState(new Set());
  const [waImporting, setWaImport]      = useState(false);

  const [listsModal, setListsModal]     = useState(false);
  const [otherLists, setOtherLists]     = useState([]);
  const [selectedList, setSelectedList] = useState('');
  const [merging, setMerging]           = useState(false);

  const [deleteContact, setDeleteContact] = useState(null);

  const searchTimer  = useRef(null);
  const fileInput    = useRef(null);
  const listNameRef  = useRef();
  const manualDddRef = useRef();

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchContacts = useCallback(async (off = 0, q = searchDebounced) => {
    if (isNew) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: off });
      if (q) params.append('search', q);
      const { data } = await api.get(`/lists/${id}/contacts?${params}`);
      setContacts(data.contacts);
      setTotal(data.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id, isNew, searchDebounced]);

  useEffect(() => {
    if (isAdmin && isNew) navigate('/listas', { replace: true });
  }, [isAdmin, isNew, navigate]);

  useEffect(() => {
    if (isNew && isAdmin) {
      api.get('/tenants').then(({ data }) => setTenants(data)).catch(() => {});
    }
  }, [isNew, isAdmin]);

  useEffect(() => { if (isNew) setTimeout(() => listNameRef.current?.focus(), 100); }, [isNew]);
  useEffect(() => { if (manualModal) setTimeout(() => manualDddRef.current?.focus(), 100); }, [manualModal]);

  useEffect(() => {
    if (isNew) return;
    api.get('/lists').then(({ data }) => {
      const found = data.find(l => String(l.id) === id);
      if (found) setName(found.name);
    }).catch(() => {});
    fetchContacts(0, '');
  }, [id, isNew, fetchContacts]);

  function handleSearchChange(val) {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchD(val);
      setOffset(0);
      fetchContacts(0, val);
    }, 400);
  }

  // ── Salvar lista (nova) ───────────────────────────────────────────────────

  async function handleSave() {
    if (!name.trim()) return;
    if (isAdmin && !tenantId) return alertErro('Cliente obrigatório', 'Selecione um cliente para criar a lista');
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        tenant_id: isAdmin ? parseInt(tenantId) : user?.tenant_id,
      };
      const { data } = await api.post('/lists', body);
      navigate(`/listas/${data.id}`, { replace: true });
    } catch (e) {
      alertErro('Erro ao criar lista', e.response?.data?.error || 'Não foi possível criar a lista');
    } finally {
      setSaving(false);
    }
  }

  async function handleRename() {
    try {
      await api.put(`/lists/${id}`, { name: name.trim() });
    } catch {}
  }

  // ── Contato manual ───────────────────────────────────────────────────────

  async function handleManual(e) {
    e.preventDefault();
    setManualError('');
    setManualLoad(true);
    try {
      await api.post(`/lists/${id}/contacts/manual`, manualForm);
      setManualModal(false);
      setManualForm({ ddd: '', numero: '', name: '', email: '' });
      fetchContacts(offset, searchDebounced);
      showNotice('success', 'Contato adicionado com sucesso.');
    } catch (err) {
      setManualError(err.response?.data?.error || 'Erro ao adicionar');
    } finally {
      setManualLoad(false);
    }
  }

  // ── Excluir contato ───────────────────────────────────────────────────────

  async function handleDeleteContact() {
    try {
      await api.delete(`/lists/${id}/contacts/${deleteContact.id}`);
      setDeleteContact(null);
      fetchContacts(offset, searchDebounced);
    } catch (e) {
      alertErro('Erro ao excluir', e.response?.data?.error || 'Não foi possível remover o contato');
    }
  }

  // ── Importar arquivo ─────────────────────────────────────────────────────

  async function handleFileSelect(file) {
    if (!file) return;
    if (fileInput.current) fileInput.current.value = '';

    setFilePending(file);
    setFilePreview(null);
    setFileModal(false);
    setFileUploading(true);

    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/lists/${id}/contacts/import/preview`, fd);

      const detected = data.detected || {};
      const newColMap = {
        nameIdx:  Number.isInteger(detected.nameIdx)  ? detected.nameIdx  : -1,
        phoneIdx: Number.isInteger(detected.phoneIdx) ? detected.phoneIdx : -1,
        emailIdx: Number.isInteger(detected.emailIdx) ? detected.emailIdx : -1,
      };

      setColMap(newColMap);
      setFilePreview(data);
      setFileModal(true);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Verifique se o arquivo CSV/Excel é válido e tente novamente';
      console.error('[importPreview]', e.response?.status, msg);
      alertErro('Erro ao processar arquivo', msg);
    } finally {
      setFileUploading(false);
    }
  }

  async function confirmFileImport() {
    if (!filePending) return;
    setFileUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', filePending);
      fd.append('colMap', JSON.stringify(colMap));
      const { data } = await api.post(`/lists/${id}/contacts/import/file`, fd);
      setFileModal(false);
      setFilePending(null);
      setFilePreview(null);
      if (fileInput.current) fileInput.current.value = '';
      fetchContacts(0, searchDebounced);
      const type = data.invalid > 0 ? 'warn' : 'success';
      showNotice(type, data.message);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Não foi possível importar os contatos do arquivo';
      console.error('[importFile]', e.response?.status, msg);
      alertErro('Erro ao importar', msg);
    } finally {
      setFileUploading(false);
    }
  }

  // ── Importar grupos ──────────────────────────────────────────────────────

  async function openGroupModal() {
    setGroupModal(true);
    setGroups([]);
    setSelectedGroup('');
    setGroupsLoad(true);
    try {
      const { data } = await api.post(`/lists/${id}/contacts/import/whatsapp-groups`, {});
      setGroups(data.groups || []);
    } catch (e) {
      alertErro('Erro ao buscar grupos', e.response?.data?.error || 'Não foi possível listar os grupos WhatsApp');
      setGroupModal(false);
    } finally {
      setGroupsLoad(false);
    }
  }

  async function handleImportGroup() {
    if (!selectedGroup) return;
    setGroupImport(true);
    try {
      const { data } = await api.post(`/lists/${id}/contacts/import/whatsapp-groups`, { groupId: selectedGroup });
      setGroupModal(false);
      fetchContacts(0, searchDebounced);
      showNotice('success', `${data.added} contatos importados do grupo.`);
    } catch (e) {
      alertErro('Erro ao importar grupo', e.response?.data?.error || 'Não foi possível importar membros do grupo');
    } finally {
      setGroupImport(false);
    }
  }

  // ── Importar contatos da agenda ─────────────────────────────────────────

  async function openWaModal() {
    setWaModal(true);
    setWaContacts([]);
    setWaSelected(new Set());
    setWaSearch('');
    setWaLoad(true);
    try {
      const { data } = await api.post(`/lists/${id}/contacts/import/whatsapp-contacts`, {});
      setWaContacts(data.contacts || []);
    } catch (e) {
      alertErro('Erro ao buscar contatos', e.response?.data?.error || 'Não foi possível carregar os contatos da agenda');
      setWaModal(false);
    } finally {
      setWaLoad(false);
    }
  }

  async function handleImportWa() {
    setWaImport(true);
    try {
      const selected = waContacts.filter(c => waSelected.has(c.jid));
      const { data } = await api.post(`/lists/${id}/contacts/import/whatsapp-contacts`, { phones: selected });
      setWaModal(false);
      fetchContacts(0, searchDebounced);
      showNotice('success', `${data.added} contatos importados da agenda.`);
    } catch (e) {
      alertErro('Erro ao importar contatos', e.response?.data?.error || 'Não foi possível importar os contatos selecionados');
    } finally {
      setWaImport(false);
    }
  }

  function toggleWaContact(jid) {
    setWaSelected(prev => {
      const next = new Set(prev);
      next.has(jid) ? next.delete(jid) : next.add(jid);
      return next;
    });
  }

  // ── Mesclar listas ───────────────────────────────────────────────────────

  async function openListsModal() {
    setListsModal(true);
    setSelectedList('');
    try {
      const { data } = await api.get('/lists');
      setOtherLists(data.filter(l => String(l.id) !== id));
    } catch {}
  }

  async function handleMerge() {
    if (!selectedList) return;
    setMerging(true);
    try {
      const { data } = await api.post(`/lists/${id}/contacts/import/existing-list`, { source_list_id: selectedList });
      setListsModal(false);
      fetchContacts(0, searchDebounced);
      showNotice('success', `${data.added} contatos mesclados com sucesso.`);
    } catch (e) {
      alertErro('Erro ao mesclar', e.response?.data?.error || 'Não foi possível mesclar as listas');
    } finally {
      setMerging(false);
    }
  }

  // ── Exportar ─────────────────────────────────────────────────────────────

  function handleExport(fmt) {
    const token = localStorage.getItem('prospect_token');
    window.open(`/prospect/lists/${id}/contacts/export?format=${fmt}&token=${token}`, '_blank');
    setExportDrop(false);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  function showNotice(type, msg) {
    setNotice({ type, msg });
    setTimeout(() => setNotice(null), 8000);
  }

  const waFiltered = waContacts.filter(c =>
    !waSearch || c.name.toLowerCase().includes(waSearch.toLowerCase()) || c.phone.includes(waSearch)
  );

  const canCreate = isNew && name.trim() && (!isAdmin || tenantId);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Breadcrumbs mainTitle={isNew ? 'Nova Lista' : name || 'Lista'} parent='Topo Prospect' title='Listas' />
      <Container fluid>

        {/* Header */}
        <div className='d-flex align-items-center gap-3 mb-4'>
          <Button color='light' size='sm' onClick={() => navigate('/listas')}>
            <ArrowLeft size={16} />
          </Button>
          <div className='flex-grow-1'>
            <Input
              type='text'
              innerRef={listNameRef}
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={() => !isNew && name.trim() && handleRename()}
              placeholder='Nome da lista (obrigatório)'
              className='border-0 shadow-none fw-bold h4 mb-0 px-0'
              style={{ fontSize: '1.3rem', background: 'transparent' }}
            />
          </div>
          {isNew ? (
            <Button color='primary' disabled={!canCreate || saving} onClick={handleSave}>
              {saving ? 'Criando...' : 'Criar lista'}
            </Button>
          ) : (
            <div className='d-flex gap-2'>
              {!isAdmin && (
                <>
                  {/* Importar dropdown */}
                  <ButtonDropdown isOpen={importDropOpen} toggle={() => setImportDrop(d => !d)}>
                    <DropdownToggle color='light' size='sm' caret>
                      <Upload size={14} className='me-1' /> Importar
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem onClick={() => { openGroupModal(); setImportDrop(false); }}>
                        <Users size={14} className='me-2' /> Grupo de WhatsApp
                      </DropdownItem>
                      <DropdownItem onClick={() => { openWaModal(); setImportDrop(false); }}>
                        <Users size={14} className='me-2' /> Contatos do WhatsApp
                      </DropdownItem>
                      <DropdownItem onClick={() => { fileInput.current?.click(); setImportDrop(false); }}>
                        <Upload size={14} className='me-2' /> Arquivo CSV/Excel
                      </DropdownItem>
                      <DropdownItem onClick={() => { openListsModal(); setImportDrop(false); }}>
                        <ChevronDown size={14} className='me-2' /> Lista já cadastrada
                      </DropdownItem>
                    </DropdownMenu>
                  </ButtonDropdown>

                  {/* Exportar dropdown */}
                  <ButtonDropdown isOpen={exportDropOpen} toggle={() => setExportDrop(d => !d)}>
                    <DropdownToggle color='light' size='sm' caret>
                      <Download size={14} className='me-1' /> Exportar
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem onClick={() => handleExport('csv')}>CSV</DropdownItem>
                      <DropdownItem onClick={() => handleExport('txt')}>TXT (números)</DropdownItem>
                    </DropdownMenu>
                  </ButtonDropdown>

                  <Button color='primary' size='sm' onClick={() => setManualModal(true)}>
                    <Plus size={14} className='me-1' /> Novo contato
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Aviso de validação */}
        {notice && (
          <Alert color={notice.type === 'warn' ? 'warning' : 'success'} className='d-flex align-items-center gap-2 mb-3'>
            <AlertCircle size={16} />
            <span className='flex-grow-1'>{notice.msg}</span>
            <Button close onClick={() => setNotice(null)} />
          </Alert>
        )}

        {/* Aviso nova lista */}
        {isNew && (
          <>
            {isAdmin && (
              <FormGroup className='mb-3'>
                <Label className='fw-medium small'>Cliente (tenant) *</Label>
                <Input type='select' bsSize='sm' value={tenantId} onChange={e => setTenantId(e.target.value)}>
                  <option value=''>Selecione o cliente...</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Input>
              </FormGroup>
            )}
            <Alert color='info' className='mb-4'>
              Dê um nome à lista e clique em <strong>Criar lista</strong>. Após criar, você poderá adicionar contatos.
            </Alert>
          </>
        )}

        {/* Contatos */}
        {!isNew && (
          <>
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <p className='mb-0 small text-muted'>
                <strong className='text-dark'>{totalContacts.toLocaleString('pt-BR')}</strong> contatos
              </p>
              <div className='position-relative' style={{ width: 260 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
                <Input
                  type='text'
                  placeholder='Buscar por nome ou número...'
                  value={search}
                  onChange={e => handleSearchChange(e.target.value)}
                  bsSize='sm'
                  style={{ paddingLeft: 32 }}
                />
              </div>
            </div>

            <Card>
              <CardBody className='p-0'>
                {loading ? (
                  <div className='p-5 text-center text-muted d-flex align-items-center justify-content-center gap-2'>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Carregando...
                  </div>
                ) : contacts.length === 0 ? (
                  <div className='p-5 text-center'>
                    <Users size={36} className='text-muted mb-3' />
                    <p className='text-muted small'>Nenhum contato {search ? 'encontrado' : 'nesta lista'}</p>
                    {!search && (
                      <Button color='link' size='sm' onClick={() => setManualModal(true)}>
                        Adicionar primeiro contato
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className='table-responsive'>
                    <Table hover className='mb-0'>
                      <thead className='table-light'>
                        <tr>
                          <th className='small text-muted text-uppercase'>Nome</th>
                          <th className='small text-muted text-uppercase'>Número</th>
                          <th className='small text-muted text-uppercase'>E-mail</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map(c => (
                          <tr key={c.id}>
                            <td className='small'>{c.name || <span className='text-muted'>—</span>}</td>
                            <td className='small font-monospace'>{c.phone}</td>
                            <td className='small text-muted'>{c.email || <span className='text-muted'>—</span>}</td>
                            <td className='text-end'>
                              {!isAdmin && (
                                <Button color='light' size='sm' onClick={() => setDeleteContact(c)}>
                                  <Trash2 size={14} />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                {/* Paginação */}
                {totalContacts > PAGE_SIZE && (
                  <div className='px-3 py-2 border-top d-flex align-items-center justify-content-between small text-muted'>
                    <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, totalContacts)} de {totalContacts}</span>
                    <div className='d-flex gap-2'>
                      <Button size='sm' color='light' disabled={offset === 0}
                        onClick={() => { setOffset(o => o - PAGE_SIZE); fetchContacts(offset - PAGE_SIZE, searchDebounced); }}>
                        ← Anterior
                      </Button>
                      <Button size='sm' color='light' disabled={offset + PAGE_SIZE >= totalContacts}
                        onClick={() => { setOffset(o => o + PAGE_SIZE); fetchContacts(offset + PAGE_SIZE, searchDebounced); }}>
                        Próxima →
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </>
        )}

        {/* Input arquivo oculto */}
        <input ref={fileInput} type='file' accept='.csv,.xlsx,.xls' style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0])} />

      </Container>

      {/* ── Loading overlay de preview ────────────────────────────────────── */}
      {fileUploading && !fileModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1055 }}>
          <div className='bg-white rounded shadow p-4 d-flex align-items-center gap-3'>
            <RefreshCw size={22} style={{ animation: 'spin 1s linear infinite' }} className='text-primary' />
            <div>
              <p className='mb-0 fw-semibold small'>Processando arquivo...</p>
              <p className='mb-0 text-muted' style={{ fontSize: '0.75rem' }}>Analisando colunas e preparando prévia</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal contato manual ──────────────────────────────────────────── */}
      <Modal isOpen={manualModal} toggle={() => setManualModal(false)}>
        <ModalHeader toggle={() => setManualModal(false)}>Adicionar contato</ModalHeader>
        <ModalBody>
          {manualError && <Alert color='danger' className='small'>{manualError}</Alert>}
          <form onSubmit={handleManual}>
            <div className='d-flex gap-2 mb-2'>
              <div style={{ width: 100 }}>
                <FormGroup>
                  <Label className='small fw-medium'>DDD</Label>
                  <Input type='text' maxLength={3} placeholder='63' bsSize='sm' innerRef={manualDddRef} value={manualForm.ddd}
                    onChange={e => setManualForm(f => ({ ...f, ddd: e.target.value.replace(/\D/g, '') }))} />
                </FormGroup>
              </div>
              <div className='flex-grow-1'>
                <FormGroup>
                  <Label className='small fw-medium'>Número *</Label>
                  <Input type='text' required placeholder='9 8765-4321' bsSize='sm' value={manualForm.numero}
                    onChange={e => setManualForm(f => ({ ...f, numero: e.target.value.replace(/\D/g, '') }))} />
                </FormGroup>
              </div>
            </div>
            <FormGroup>
              <Label className='small fw-medium'>Nome</Label>
              <Input type='text' bsSize='sm' placeholder='Nome do contato' value={manualForm.name}
                onChange={e => setManualForm(f => ({ ...f, name: e.target.value }))} />
            </FormGroup>
            <FormGroup>
              <Label className='small fw-medium'>E-mail</Label>
              <Input type='email' bsSize='sm' placeholder='contato@email.com' value={manualForm.email}
                onChange={e => setManualForm(f => ({ ...f, email: e.target.value }))} />
            </FormGroup>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setManualModal(false)}>Cancelar</Button>
          <Button color='primary' disabled={manualLoading} onClick={handleManual}>
            {manualLoading ? 'Adicionando...' : 'Adicionar'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal preview arquivo ─────────────────────────────────────────── */}
      <Modal isOpen={fileModal && !!filePreview} toggle={() => setFileModal(false)} size='lg'>
        <ModalHeader toggle={() => setFileModal(false)}>
          Prévia do arquivo — {filePreview?.total} {filePreview?.total === 1 ? 'linha' : 'linhas'}
        </ModalHeader>
        <ModalBody>
          {filePreview?.auto_detected_headers && (
            <Alert color='warning' className='small mb-3'>
              Cabeçalho não encontrado — colunas detectadas automaticamente pelo conteúdo. Verifique o mapeamento abaixo antes de importar.
            </Alert>
          )}

          {/* Mapeamento de colunas */}
          <div className='row g-2 mb-3'>
            {[['Nome', 'nameIdx'], ['Número *', 'phoneIdx'], ['E-mail', 'emailIdx']].map(([label, key]) => {
              const options = filePreview?.mappable_headers?.length > 0
                ? filePreview.mappable_headers.map(idx => ({ idx, label: translateHeader(filePreview.headers[idx]) }))
                : (filePreview?.headers || []).map((h, idx) => ({ idx, label: translateHeader(h) }));
              return (
                <div key={key} className='col-md-4'>
                  <FormGroup>
                    <Label className='small fw-medium'>{label}</Label>
                    <Input
                      type='select'
                      bsSize='sm'
                      value={colMap[key]}
                      onChange={e => setColMap(m => ({ ...m, [key]: Number(e.target.value) }))}
                    >
                      <option value={-1}>— Ignorar —</option>
                      {options.map(({ idx, label: lbl }) => (
                        <option key={idx} value={idx}>{lbl}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </div>
              );
            })}
          </div>

          {/* Nota sobre colunas extras */}
          {filePreview?.extra_headers?.length > 0 && (
            <p className='text-muted small mb-3'>
              {filePreview.extra_headers.length === 1
                ? `A coluna "${translateHeader(filePreview.headers[filePreview.extra_headers[0]])}" será salva automaticamente em dados extras.`
                : `As colunas ${filePreview.extra_headers.map(i => `"${translateHeader(filePreview.headers[i])}"`).join(', ')} serão salvas automaticamente em dados extras.`}
            </p>
          )}

          {/* Preview tabela */}
          <div className='table-responsive border rounded' style={{ maxHeight: 320 }}>
            <Table size='sm' className='mb-0'>
              <thead className='table-light sticky-top'>
                <tr>
                  {(filePreview?.headers || []).map((h, i) => (
                    <th key={i} className='small text-muted text-nowrap'>{translateHeader(h)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(filePreview?.preview || []).map((row, i) => (
                  <tr key={i}>
                    {(filePreview?.headers || []).map((h, j) => (
                      <td key={j} className='small' style={{ maxWidth: 200 }}>
                        <span className='d-inline-block text-truncate' style={{ maxWidth: 190 }}>{String(row[h] ?? '')}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {filePreview && filePreview.preview.length < filePreview.total && (
            <p className='text-muted text-center small mt-2 mb-0'>
              Exibindo {filePreview.preview.length} de {filePreview.total} linhas — todas serão importadas.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setFileModal(false)}>Cancelar</Button>
          <Button
            color='primary'
            disabled={colMap.phoneIdx === -1 || fileUploading}
            onClick={confirmFileImport}
          >
            {fileUploading ? 'Importando...' : `Confirmar importação (${filePreview?.total?.toLocaleString('pt-BR')} linhas)`}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal grupos WhatsApp ─────────────────────────────────────────── */}
      <Modal isOpen={groupModal} toggle={() => setGroupModal(false)}>
        <ModalHeader toggle={() => setGroupModal(false)}>Importar de grupo WhatsApp</ModalHeader>
        <ModalBody>
          {groupsLoading ? (
            <div className='py-4 text-center text-muted d-flex align-items-center justify-content-center gap-2'>
              <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Buscando grupos...
            </div>
          ) : groups.length === 0 ? (
            <p className='text-muted small text-center py-3'>Nenhum grupo encontrado na instância conectada.</p>
          ) : (
            <div className='d-flex flex-column gap-2' style={{ maxHeight: 260, overflowY: 'auto' }}>
              {groups.map(g => {
                const sel = selectedGroup === g.id;
                return (
                  <label key={g.id} onClick={() => setSelectedGroup(g.id)} className='rounded' style={{
                    cursor: 'pointer', color: '#212529',
                    display: 'flex', alignItems: 'center', padding: '12px 16px',
                    border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
                    background: sel ? '#f0eeff' : '#fff',
                  }}>
                    <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginRight: 12,
                      border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
                      background: sel ? '#7366ff' : '#fff',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {sel && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                    </span>
                    <div>
                      <p className='mb-0 fw-medium small' style={{ color: '#212529' }}>{g.name}</p>
                      <p className='mb-0' style={{ fontSize: '0.75rem', color: '#6c757d' }}>{g.size} membros</p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setGroupModal(false)}>Cancelar</Button>
          <Button color='primary' disabled={!selectedGroup || groupImporting} onClick={handleImportGroup}>
            {groupImporting ? 'Importando...' : 'Importar membros'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal contatos WhatsApp ───────────────────────────────────────── */}
      <Modal isOpen={waModal} toggle={() => setWaModal(false)}>
        <ModalHeader toggle={() => setWaModal(false)}>Contatos do WhatsApp</ModalHeader>
        <ModalBody>
          {waLoading ? (
            <div className='py-4 text-center text-muted d-flex align-items-center justify-content-center gap-2'>
              <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Carregando agenda...
            </div>
          ) : (
            <>
              <div className='position-relative mb-3'>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
                <Input type='text' bsSize='sm' placeholder='Buscar contato...' value={waSearch} onChange={e => setWaSearch(e.target.value)} style={{ paddingLeft: 32 }} />
              </div>
              <p className='text-muted small mb-2'>{waSelected.size} selecionados</p>
              <div style={{ maxHeight: 300, overflowY: 'auto' }} className='d-flex flex-column gap-1'>
                {waFiltered.map(c => {
                  const sel = waSelected.has(c.jid);
                  return (
                    <label key={c.jid} onClick={() => toggleWaContact(c.jid)} className='rounded' style={{
                      cursor: 'pointer', color: '#212529',
                      display: 'flex', alignItems: 'center', padding: '12px 16px',
                      border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
                      background: sel ? '#f0eeff' : '#fff',
                    }}>
                      <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginRight: 12,
                        border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
                        background: sel ? '#7366ff' : '#fff',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        {sel && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                      </span>
                      <div>
                        <p className='mb-0 fw-medium small' style={{ color: '#212529' }}>{c.name || '—'}</p>
                        <p className='mb-0 font-monospace' style={{ fontSize: '0.75rem', color: '#6c757d' }}>{c.phone}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setWaModal(false)}>Cancelar</Button>
          <Button color='primary' disabled={waSelected.size === 0 || waImporting} onClick={handleImportWa}>
            {waImporting ? 'Adicionando...' : `Adicionar ${waSelected.size > 0 ? waSelected.size : ''}`}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal listas existentes ───────────────────────────────────────── */}
      <Modal isOpen={listsModal} toggle={() => setListsModal(false)}>
        <ModalHeader toggle={() => setListsModal(false)}>Mesclar com lista existente</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className='small fw-medium'>Selecione a lista de origem</Label>
            <Input type='select' bsSize='sm' value={selectedList} onChange={e => setSelectedList(e.target.value)}>
              <option value=''>Selecione uma lista...</option>
              {otherLists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.total_contacts} contatos)</option>)}
            </Input>
          </FormGroup>
          <p className='text-muted small'>Contatos duplicados serão ignorados automaticamente.</p>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setListsModal(false)}>Cancelar</Button>
          <Button color='primary' disabled={!selectedList || merging} onClick={handleMerge}>
            {merging ? 'Mesclando...' : 'Mesclar'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal excluir contato ─────────────────────────────────────────── */}
      <Modal isOpen={!!deleteContact} toggle={() => setDeleteContact(null)}>
        <ModalHeader toggle={() => setDeleteContact(null)}>Remover contato?</ModalHeader>
        <ModalBody>
          <p className='text-muted small'>
            O número <strong className='font-monospace'>{deleteContact?.phone}</strong> será removido desta lista.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setDeleteContact(null)}>Cancelar</Button>
          <Button color='danger' onClick={handleDeleteContact}>Remover</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
